import { MOTION_EASE } from '@/components/common/motion';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

/** How long the reveal runs, in ms. Mirrored in the `duration-[800ms]` class below. */
const REVEAL_MS = 800;

/** Safety net for the class cleanup: `transitionend` does not fire if the element never animates. */
const CLEANUP_MS = REVEAL_MS + 200;

/** How far below the viewport edge a block starts revealing. */
const ROOT_MARGIN = '0px 0px -10% 0px';

type RevealPhase = 'hidden' | 'revealing' | 'settled';

interface RevealProps {
  children: ReactNode;
  /** Staggers the start against sibling blocks, in ms. */
  delayMs?: number;
  className?: string;
}

/**
 * Reveals a block the first time it enters the viewport.
 *
 * Deliberately an `IntersectionObserver` rather than a scroll listener: the observer fires off
 * the main thread and does not re-run on every frame of a scroll.
 *
 * The important part is the third phase. While revealing, the wrapper carries a transform and a
 * blur filter, and both of those make it a containing block for `position: fixed` *and* a new
 * stacking context — which is exactly what the pinned table columns and their z-index ordering
 * inside rely on not happening. So once the transition is done the classes are dropped entirely
 * and the wrapper goes back to being an inert `div`.
 */
export const Reveal: FC<RevealProps> = ({ children, delayMs = 0, className = '' }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<RevealPhase>('hidden');

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Respect the OS setting before observing anything: there is nothing to animate, and the
    // content must not depend on an observer callback to become visible.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setPhase('settled');
      return;
    }

    let startTimer: ReturnType<typeof setTimeout> | undefined;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        startTimer = setTimeout(() => {
          setPhase('revealing');
          settleTimer = setTimeout(() => setPhase('settled'), CLEANUP_MS);
        }, delayMs);
      },
      { rootMargin: ROOT_MARGIN }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (startTimer) clearTimeout(startTimer);
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, [delayMs]);

  // Settled carries no transform, no filter, no clipping and no transition — see the note above.
  //
  // `overflow-hidden` matters while the transform is on: a transformed wrapper leaks the layout
  // width of everything inside it — including a wide table that is meant to scroll inside its
  // own container — into the document's scroll width, which turns into a horizontally scrolling
  // page on a phone until the block reveals. Clipping keeps that contained, and it also stops the
  // blurred, offset copy painting over its neighbours on the way in.
  const phaseClass =
    phase === 'settled'
      ? ''
      : phase === 'revealing'
        ? `overflow-hidden transition-all duration-[800ms] ${MOTION_EASE} translate-y-0 opacity-100 blur-0`
        : `overflow-hidden transition-all duration-[800ms] ${MOTION_EASE} translate-y-16 opacity-0 blur-md`;

  return (
    <div
      ref={ref}
      className={`${phaseClass} ${className}`.trim()}
      onTransitionEnd={() => setPhase((current) => (current === 'revealing' ? 'settled' : current))}
    >
      {children}
    </div>
  );
};
